import type { FC, ReactNode } from 'react'
import { createContext, useCallback, useContext, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  useMutation as useConnectMutation,
  useTransport,
} from '@connectrpc/connect-query'
import type { MessageDescriptor } from '@lingui/core'
import { msg, plural, t } from '@lingui/core/macro'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { useAuthenticated } from '@jotta/auth-client/useAuthStatus'
import { useBrandName } from '@jotta/grpc-connect-client/config'
import { authTransport } from '@jotta/grpc-connect-client/transport'
import { Album_AlbumAction } from '@jotta/grpc-connect-openapi/album'
import {
  addPhotosToAlbum as addPhotosToAlbumDescriptor,
  deleteAlbum as deleteAlbumDescriptor,
  followAlbum as followAlbumDescriptor,
  removePhotosFromAlbum as removePhotosFromAlbumDescriptor,
  setCoverPhoto as setCoverPhotoDescriptor,
  sharePhotos as sharePhotosDescriptor,
  unshareAlbum as unshareAlbumDescriptor,
} from '@jotta/grpc-connect-openapi/albumQuery'
import {
  PatchPhotoRequest_StateChange,
  Photo_PhotoAction,
} from '@jotta/grpc-connect-openapi/photo'
import { deletePhoto as deletePhotoDescriptor } from '@jotta/grpc-connect-openapi/photoQuery'
import type { BrandThemeIcon } from '@jotta/types/Brand'
import SvgPhoto from '@jotta/ui/SvgPhoto'
import { showToast } from '@jotta/ui/ToastProvider'
import { popPath } from '@jotta/utils/pathUtils'

import { downloadPhotos } from '../../api/photosApi'
import {
  getAlbumQuery,
  invalidateAlbumQueries,
  useAlbum,
} from '../../api/useAlbum'
import type { CollectionRef } from '../../api/useCollection'
import { useAlbumId, useCollection } from '../../api/useCollection'
import { usePatchPhotos } from '../../api/usePatchPhotos'
import {
  resolveSelectablePhoto,
  usePhotoFetcher,
  usePhotoRef,
  usePhotos,
} from '../../api/usePhoto'
import { usePhotoStore, useSelectionStore } from '../../store/PhotoContext'
import type { SelectablePhoto } from '../../store/SelectionStore'
import { DeleteAlbumDialog } from '../Album/Dialogs/DeleteAlbumDialog'
import { DeletePhotoDialog } from '../Album/Dialogs/DeletePhotoDialog'
import { HidePhotoDialog } from '../Album/Dialogs/HidePhotoDialog'
import { LeaveAlbumDialog } from '../Album/Dialogs/LeaveAlbumDialog'
import { LoginToContinueDialog } from '../Album/Dialogs/LoginToContinueDialog'
import { RemovePhotoDialog } from '../Album/Dialogs/RemovePhotoDialog'
import { SelectAlbumDialog } from '../Album/Dialogs/SelectAlbumDialog'
import { ShareAlbumDialog } from '../Album/Dialogs/ShareAlbumDialog'
import { SharePhotosDialog } from '../Album/Dialogs/SharePhotosDialog'
import { useAddPhotoMode } from '../PhotoGallery/useAddPhotoMode'

import { photoActionsApi } from './PhotoActions.config'
import type { PhotoActionType } from './PhotoActions.types'

type ActionLabel =
  | MessageDescriptor
  | string
  | ((count: number) => MessageDescriptor | string)

type Action = {
  icon: BrandThemeIcon
  label: ActionLabel
  testid?: string
}
type PhotoActionScope = 'default' | 'people'
type ActionPhoto = SelectablePhoto & { fileName?: string }

export type ActionDispatcherProps = {
  onClick: () => void
  loading: boolean
  label: string
  icon: BrandThemeIcon
  testid: string | undefined
}

const {
  ADD_PHOTOS,
  ADD_PHOTOS_TO_ALBUM,
  REMOVE_PHOTOS_FROM_ALBUM,
  SHARE_PHOTO,
  DELETE_PHOTOS,
  HIDE_PHOTO,
  UNHIDE_PHOTO,
  FOLLOW_ALBUM,
  LEAVE_ALBUM,
  SHARE_ALBUM,
  DELETE_ALBUM,
  DOWNLOAD_PHOTOS,
} = photoActionsApi

function setCoverPhotoAction(): Action {
  return {
    icon: SvgPhoto,
    label: msg`Set as cover photo`,
    testid: 'ACTION_SET_COVER_PHOTO',
  }
}

const downloadPhotosAction: Action = {
  icon: DOWNLOAD_PHOTOS.icon,
  label: count =>
    count > 1 ? t`Download ${count} items` : DOWNLOAD_PHOTOS.label,
  testid: DOWNLOAD_PHOTOS.testid,
}

const removePhotosFromAlbumAction: Action = {
  icon: REMOVE_PHOTOS_FROM_ALBUM.icon,
  label: count =>
    count > 1 ? t`Remove ${count} items` : REMOVE_PHOTOS_FROM_ALBUM.label,
  testid: REMOVE_PHOTOS_FROM_ALBUM.testid,
}

function translateLabel(action: Action, count = 1) {
  const label =
    typeof action.label === 'function' ? action.label(count) : action.label
  return typeof label === 'string' ? label : t(label)
}

const PhotoActionContext = createContext<
  | {
      collectionRef: CollectionRef
      photos: readonly SelectablePhoto[]
      photoIds: readonly string[]
      actionScope: PhotoActionScope
      resolveActionPhotos: () => Promise<readonly ActionPhoto[]>
      ActionDispatcher: FC<{
        loading: boolean
        onClick: () => void
        action: Action
      }>
      setActiveActionHandler: (handler: FC, onSuccess?: () => void) => void
      clearActiveAction: (success: boolean) => void
      trackAction: (actionType: PhotoActionType) => void
    }
  | undefined
>(undefined)
function usePhotoActionContext() {
  const context = useContext(PhotoActionContext)
  if (!context) {
    throw new Error('must be used within PhotoActionProvider')
  }
  return context
}

function PhotoActionProvider({
  collectionRef,
  photos,
  actionDispatcherComponent: ActionDispatcherComponent,
  actionScope = 'default',
  onTrackAction,
  children,
}: {
  collectionRef: CollectionRef
  photos: readonly SelectablePhoto[]
  actionDispatcherComponent: FC<ActionDispatcherProps>
  actionScope?: PhotoActionScope
  onTrackAction?: (actionType: PhotoActionType) => void
  children?: ReactNode
}) {
  const [ActiveActionHandler, setActiveActionHandler] = useState<
    FC | undefined
  >()
  const [onSuccess, setOnSuccess] = useState<() => void>()
  const [addPhotoModeAlbumId] = useAddPhotoMode()
  const transport = useTransport()
  const queryClient = useQueryClient()
  const resolveActionPhotos = useCallback(async () => {
    if (actionScope !== 'people') {
      return photos
    }

    return Promise.all(
      photos.map(photo =>
        photo.encodedContentRef
          ? photo
          : resolveSelectablePhoto(transport, queryClient, photo),
      ),
    )
  }, [actionScope, photos, queryClient, transport])

  return (
    <PhotoActionContext.Provider
      value={{
        collectionRef,
        photos,
        photoIds: photos.map(photo => photo.id),
        actionScope,
        resolveActionPhotos,
        ActionDispatcher: ({ action, ...props }) => (
          <ActionDispatcherComponent
            {...props}
            label={translateLabel(action, photos.length)}
            icon={action.icon}
            testid={action.testid}
          />
        ),
        setActiveActionHandler: (handler, onSuccess) => {
          setActiveActionHandler(() => handler)
          setOnSuccess(() => onSuccess)
        },
        clearActiveAction: success => {
          setActiveActionHandler(undefined)
          if (success && onSuccess) {
            onSuccess()
          }
        },
        trackAction: actionType => onTrackAction?.(actionType),
      }}
    >
      {ActiveActionHandler && <ActiveActionHandler />}
      {addPhotoModeAlbumId ? (
        <AddPhotosHandler albumId={addPhotoModeAlbumId} />
      ) : (
        <div className="flex gap-2">{children}</div>
      )}
    </PhotoActionContext.Provider>
  )
}

function isApplicableToAllPhotos(
  action: Photo_PhotoAction,
  photos: { actions: Photo_PhotoAction[] }[],
) {
  return (
    photos.length > 0 && photos.every(photo => photo.actions.includes(action))
  )
}

function getPhotoRefMd5(photo: SelectablePhoto) {
  return photo.ref?.ref.case === 'md5' ? photo.ref.ref.value : undefined
}

function getPhotoMd5(
  photo: SelectablePhoto,
  { allowSparseMd5 = false }: { allowSparseMd5?: boolean } = {},
) {
  return allowSparseMd5
    ? (photo.md5 ?? getPhotoRefMd5(photo))
    : getPhotoRefMd5(photo)
}

function getContextPhotoMd5s(
  photos: readonly SelectablePhoto[],
  options?: { allowSparseMd5?: boolean },
) {
  const photoMd5s = photos.map(photo => getPhotoMd5(photo, options))
  return photoMd5s.every((md5): md5 is string => typeof md5 === 'string')
    ? photoMd5s
    : undefined
}

function DeletePhotosHandler() {
  const { clearActiveAction, collectionRef, photoIds } = usePhotoActionContext()
  const { fetchPhoto } = usePhotoFetcher(collectionRef)
  const { mutateAsync: deletePhoto } = useConnectMutation(deletePhotoDescriptor)
  const photoStore = usePhotoStore()
  const selectionStore = useSelectionStore()
  const { mutate: deletePhotos, isPending } = useMutation({
    onSuccess: () => {
      selectionStore.delete(...photoIds)
      clearActiveAction(true)
    },
    mutationFn: () =>
      Promise.all(
        photoIds.map(async photoId => {
          const photo = await fetchPhoto(photoId)
          await deletePhoto({
            ref: photo.ref,
          })
          await photoStore.timeline.delete([photoId])
        }),
      ),
  })
  return (
    <DeletePhotoDialog
      open
      isLoading={isPending}
      count={photoIds.length}
      onDeletePhoto={deletePhotos}
      onClose={open => clearActiveAction(false)}
    />
  )
}

function SharePhotosHandler() {
  const {
    collectionRef,
    photos,
    photoIds,
    clearActiveAction,
    trackAction,
    actionScope,
  } = usePhotoActionContext()
  const { fetchAll } = usePhotoFetcher(collectionRef)
  const {
    data: sharePhotosResponse,
    mutateAsync: sharePhotos,
    reset,
  } = useConnectMutation(sharePhotosDescriptor, {
    transport: authTransport,
  })
  const { mutate: fetchAndSharePhotos, isPending: isPendingSharing } =
    useMutation({
      mutationFn: async () => {
        const allowSparseMd5 = actionScope === 'people'
        const photoMd5s =
          getContextPhotoMd5s(photos, { allowSparseMd5 }) ??
          (await fetchAll(photoIds)).map(photo => photo.md5)
        await sharePhotos({
          photoMd5s,
        })
        trackAction('SHARE_PHOTO')
      },
    })
  const { mutate: unshareAlbum, isPending: isPendingUnsharing } =
    useConnectMutation(unshareAlbumDescriptor, {
      transport: authTransport,
      onSuccess: () => reset(),
    })

  const { albumId, shareId } = sharePhotosResponse || {}
  return (
    <SharePhotosDialog
      forceOpen={true}
      photoCount={photoIds.length}
      shareId={shareId}
      isLoading={isPendingSharing || isPendingUnsharing}
      onSharePhoto={shared => {
        if (shared) {
          fetchAndSharePhotos()
        } else if (albumId) {
          unshareAlbum({
            albumId: albumId,
          })
        }
      }}
      triggerElement={undefined}
      onOpenChange={() => clearActiveAction(false)}
    />
  )
}

function DownloadPhotos() {
  const {
    ActionDispatcher,
    photos,
    trackAction,
    actionScope,
    resolveActionPhotos,
  } = usePhotoActionContext()
  const brandName = useBrandName()
  const authenticated = useAuthenticated() === 'TRUE'
  const firstRef =
    actionScope === 'default' && photos.length === 1 ? photos[0].ref : undefined
  const { data: firstPhoto } = usePhotoRef(firstRef, {
    enabled: authenticated && actionScope === 'default',
    throwOnError: false,
  })
  const defaultFileName = firstPhoto?.fileName
    ? firstPhoto.fileName
    : brandName
      ? brandName
      : t`Photos`
  const { mutate, isPending } = useMutation({
    throwOnError: false,
    mutationFn: async () => {
      const actionPhotos = await resolveActionPhotos()
      const refs = actionPhotos.flatMap(({ encodedContentRef }) =>
        encodedContentRef ? [encodedContentRef] : [],
      )

      if (refs.length !== actionPhotos.length) {
        throw new Error('Could not load content refs for selected photos')
      }

      const fileName =
        actionPhotos.length === 1 && actionPhotos[0].fileName
          ? actionPhotos[0].fileName
          : defaultFileName

      return downloadPhotos({
        fileName,
        refs,
      })
    },
    onSuccess: () => {
      trackAction('DOWNLOAD_PHOTOS')
    },
    onError: () => {
      showToast({
        message: t`Downloading wasn't successful.`,
        hasCloseButton: true,
        duration: 'short',
      })
    },
  })
  return (
    <ActionDispatcher
      action={downloadPhotosAction}
      loading={isPending}
      onClick={() => mutate()}
    />
  )
}

function HidePhotoHandler() {
  const { collectionRef, photoIds, clearActiveAction } = usePhotoActionContext()
  const photos = usePhotos(collectionRef, photoIds)
  const selectionStore = useSelectionStore()
  const { mutate: patchPhotos, isPending } = usePatchPhotos({
    collectionRef,
    onSuccess: () => {
      selectionStore.delete(...photoIds)
      clearActiveAction(true)
    },
  })
  return (
    isApplicableToAllPhotos(Photo_PhotoAction.HIDE, photos) && (
      <HidePhotoDialog
        open
        count={photos.length}
        isLoading={isPending}
        onClose={() => clearActiveAction(false)}
        onHidePhoto={() => {
          patchPhotos({
            photos,
            stateChange: PatchPhotoRequest_StateChange.HIDE,
          })
        }}
      />
    )
  )
}

function UnhidePhotos({ onSuccess }: { onSuccess?: () => void }) {
  const { collectionRef, ActionDispatcher, photoIds, clearActiveAction } =
    usePhotoActionContext()
  const photos = usePhotos(collectionRef, photoIds)
  const selectionStore = useSelectionStore()
  const { mutate: patchPhotos, isPending } = usePatchPhotos({
    collectionRef,
    onSuccess: () => {
      selectionStore.delete(...photoIds)
      clearActiveAction(true)
      onSuccess?.()
    },
  })
  return (
    isApplicableToAllPhotos(Photo_PhotoAction.UNHIDE, photos) && (
      <ActionDispatcher
        action={UNHIDE_PHOTO}
        loading={isPending}
        onClick={() =>
          patchPhotos({
            photos,
            stateChange: PatchPhotoRequest_StateChange.UNHIDE,
          })
        }
      />
    )
  )
}

function AddToAlbumHandler() {
  const { clearActiveAction, collectionRef } = usePhotoActionContext()
  const { mutate } = useAddPhotos()
  const currentAlbumId = useAlbumId(collectionRef)
  return (
    <SelectAlbumDialog
      open
      onClose={() => clearActiveAction(false)}
      currentAlbumId={currentAlbumId}
      addToAlbumHandler={albumId => mutate(albumId)}
    />
  )
}

function RemoveFromAlbum({ onSuccess }: { onSuccess?: () => void }) {
  const { collectionRef } = usePhotoActionContext()
  const albumId = useAlbumId(collectionRef)
  if (!albumId) {
    return null
  }
  return (
    <PhotoActionTrigger
      actionType={Photo_PhotoAction.REMOVE_FROM_ALBUM}
      action={removePhotosFromAlbumAction}
      handler={() => <RemoveFromAlbumHandler albumId={albumId} />}
      onSuccess={onSuccess}
    />
  )
}

function RemoveFromAlbumHandler({ albumId }: { albumId: string }) {
  const { clearActiveAction, collectionRef, photoIds } = usePhotoActionContext()
  const { fetchAll } = usePhotoFetcher(collectionRef)
  const selectionStore = useSelectionStore()
  const { mutateAsync: removePhotosFromAlbum } = useConnectMutation(
    removePhotosFromAlbumDescriptor,
  )
  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      const photos = await fetchAll(photoIds)
      const photoRefs = photos.map(({ ref }) => {
        if (!ref) {
          throw new Error('could not load photo ref for photo')
        }
        return ref
      })
      await removePhotosFromAlbum({
        albumId,
        photoRefs,
      })
    },
    onSuccess: async () => {
      try {
        await invalidateAlbumQueries(albumId)
      } finally {
        selectionStore.delete(...photoIds)
        clearActiveAction(true)
      }
    },
  })
  return (
    <RemovePhotoDialog
      open
      isLoading={isPending}
      count={photoIds.length}
      onClose={() => clearActiveAction(false)}
      onRemovePhoto={() => mutate()}
    />
  )
}

function UseAsCoverPhoto({
  albumId,
  canUpdateCoverPhoto: canUpdateCoverPhotoProp,
}: {
  albumId: string
  canUpdateCoverPhoto?: boolean
}) {
  const { ActionDispatcher, photoIds } = usePhotoActionContext()
  const { data: album } = getAlbumQuery({
    variables: {
      albumIdOrShareId: albumId,
    },
    enabled: canUpdateCoverPhotoProp === undefined && Boolean(albumId),
    throwOnError: false,
    select: data => data.album,
  })
  const canUpdateCoverPhoto =
    canUpdateCoverPhotoProp ??
    album?.actions?.includes(Album_AlbumAction.UPDATE_COVER_PHOTO) ??
    false
  const photoId = photoIds[0]
  const { mutate, isPending } = useConnectMutation(setCoverPhotoDescriptor, {
    onSuccess: async (_data, variables) => {
      const updatedAlbumId = variables.albumId
      if (!updatedAlbumId) {
        return
      }

      await invalidateAlbumQueries(updatedAlbumId)

      showToast({
        message: t`Album cover updated.`,
        hasCloseButton: true,
        duration: 'short',
      })
    },
    onError: () => {
      showToast({
        message: t`Updating album cover wasn't successful.`,
        hasCloseButton: true,
        duration: 'short',
      })
    },
  })

  if (photoIds.length !== 1 || !photoId || !canUpdateCoverPhoto) {
    return null
  }

  return (
    <ActionDispatcher
      action={setCoverPhotoAction()}
      loading={isPending}
      onClick={() =>
        mutate({
          albumId,
          photoId,
        })
      }
    />
  )
}

function PhotoActionTrigger({
  action,
  actionType,
  handler,
  onSuccess,
}: {
  action: Action
  actionType: Photo_PhotoAction
  handler: FC
  onSuccess?: () => void
}) {
  const photoActionContext = usePhotoActionContext()
  const {
    ActionDispatcher,
    setActiveActionHandler,
    collectionRef,
    photoIds,
    actionScope,
  } = photoActionContext
  const photos = usePhotos(collectionRef, photoIds, {
    enabled: actionScope === 'default',
  })
  // People selections carry sparse md5 refs. SelectionManager already limits
  // this scope to actions that can work from md5s, so avoid resolving every
  // selected photo just to decide action-bar visibility.
  const isVisible =
    actionScope === 'people' || isApplicableToAllPhotos(actionType, photos)

  return (
    isVisible && (
      <ActionDispatcher
        action={action}
        loading={false}
        onClick={() => setActiveActionHandler(handler, onSuccess)}
      />
    )
  )
}

export const PhotoAction = {
  Provider: PhotoActionProvider,

  DownloadPhotos,
  UseAsCoverPhoto,
  UnhidePhotos,
  RemoveFromAlbum,

  HidePhotos: ({ onSuccess }: { onSuccess?: () => void }) => (
    <PhotoActionTrigger
      action={HIDE_PHOTO}
      actionType={Photo_PhotoAction.HIDE}
      handler={HidePhotoHandler}
      onSuccess={onSuccess}
    />
  ),

  AddToAlbum: () => (
    <PhotoActionTrigger
      actionType={Photo_PhotoAction.ADD_TO_ALBUM}
      action={ADD_PHOTOS_TO_ALBUM}
      handler={AddToAlbumHandler}
    />
  ),
  SharePhotos: () => (
    <PhotoActionTrigger
      action={SHARE_PHOTO}
      actionType={Photo_PhotoAction.SHARE}
      handler={SharePhotosHandler}
    />
  ),
  DeletePhotos: ({ onSuccess }: { onSuccess?: () => void }) => (
    <PhotoActionTrigger
      actionType={Photo_PhotoAction.DELETE}
      action={DELETE_PHOTOS}
      handler={DeletePhotosHandler}
      onSuccess={onSuccess}
    />
  ),
}

type AlbumActionDispatcherProps = ActionDispatcherProps & {
  actionType: Album_AlbumAction | undefined
}

const AlbumActionContext = createContext<
  | {
      collectionRef: CollectionRef
      ActionDispatcher: FC<{
        action: Action
        actionType: Album_AlbumAction | undefined
        loading: boolean
        onClick: () => void
      }>
      albumIdOrShareId: string
      setActiveActionHandler: (handler: FC) => void
      cancelActiveAction: () => void
    }
  | undefined
>(undefined)
function useAlbumActionContext() {
  const context = useContext(AlbumActionContext)
  if (!context) {
    throw new Error('must be used within AlbumActionProvider')
  }
  return context
}

function AlbumActionDispatcher({
  actionDispatcherComponent: ActionDispatcher,
  actionType,
  ...rest
}: AlbumActionDispatcherProps & {
  actionDispatcherComponent: FC<ActionDispatcherProps>
}) {
  const { albumIdOrShareId } = useAlbumActionContext()
  const { data } = useAlbum(albumIdOrShareId)
  const displayActionDispatcher =
    !actionType || data?.actions.includes(actionType)
  return displayActionDispatcher && <ActionDispatcher {...rest} />
}

function AlbumActionProvider({
  albumIdOrShareId,
  collectionRef,
  actionDispatcherComponent,
  children,
}: {
  albumIdOrShareId: string
  collectionRef: CollectionRef
  actionDispatcherComponent: FC<ActionDispatcherProps>
  children: ReactNode
}) {
  const [ActiveActionHandler, setActiveActionHandler] = useState<FC>()

  return (
    <AlbumActionContext.Provider
      value={{
        ActionDispatcher: props => (
          <AlbumActionDispatcher
            onClick={props.onClick}
            loading={props.loading}
            label={translateLabel(props.action)}
            icon={props.action.icon}
            testid={props.action.testid}
            actionType={props.actionType}
            actionDispatcherComponent={actionDispatcherComponent}
          />
        ),
        collectionRef,
        albumIdOrShareId,
        setActiveActionHandler: handler =>
          setActiveActionHandler(() => handler),
        cancelActiveAction: () => setActiveActionHandler(undefined),
      }}
    >
      {ActiveActionHandler && <ActiveActionHandler />}
      <div className="flex gap-2">{children}</div>
    </AlbumActionContext.Provider>
  )
}

function DownloadAlbum() {
  const brandName = useBrandName()
  const { mutate, isPending: isDownloadPending } = useMutation({
    mutationFn: downloadPhotos,
  })
  const { collectionRef, ActionDispatcher } = useAlbumActionContext()
  const { data, isPending: isContentRefPending } = useCollection(collectionRef)
  const collection = data?.case === 'collection' && data.value
  const { encodedContentRef, title } = collection || {}
  const fileName = title ? title : brandName ? brandName : t`Photos`
  const isPending = isDownloadPending || isContentRefPending
  return (
    <ActionDispatcher
      action={DOWNLOAD_PHOTOS}
      actionType={undefined}
      loading={isPending}
      onClick={() =>
        mutate({
          fileName,
          refs: encodedContentRef ? [encodedContentRef] : [],
        })
      }
    />
  )
}

function LoginToContinue({ title, url }: { url: string; title: string }) {
  const { cancelActiveAction } = useAlbumActionContext()
  return (
    <LoginToContinueDialog
      open
      title={title}
      url={url}
      onClose={cancelActiveAction}
      triggerElement={undefined}
    />
  )
}

function useAddPhotos() {
  const {
    collectionRef,
    photos,
    photoIds,
    clearActiveAction,
    trackAction,
    actionScope,
  } = usePhotoActionContext()
  const navigate = useNavigate()
  const { fetchAll } = usePhotoFetcher(collectionRef)
  const currentAlbumId = useAlbumId(collectionRef)
  const selectionStore = useSelectionStore()
  const { mutateAsync: addPhotosToAlbum } = useConnectMutation(
    addPhotosToAlbumDescriptor,
    {
      onSuccess: ({ addedPhotoCount, albumId }) => {
        trackAction('ADD_PHOTOS_TO_ALBUM')
        clearActiveAction(true)
        selectionStore.clear()

        const autoNavigateToAlbum = albumId !== currentAlbumId
        if (autoNavigateToAlbum) {
          navigate(`/photo/album/${albumId}`)
        }

        showToast({
          hasCloseButton: true,
          message: plural(addedPhotoCount, {
            one: 'Added 1 photo',
            other: 'Added # photos',
          }),
          actionButton: autoNavigateToAlbum
            ? undefined
            : {
                label: t`Show album`,
                onClick: () => navigate(`/photo/album/${albumId}`),
              },
        })
      },
    },
  )
  return useMutation({
    mutationFn: async (albumId: string) => {
      const allowSparseMd5 = actionScope === 'people'
      const photoMd5s =
        getContextPhotoMd5s(photos, { allowSparseMd5 }) ??
        (await fetchAll(photoIds)).map(photo => photo.md5)
      await addPhotosToAlbum({
        album: {
          case: 'existingAlbumId',
          value: albumId,
        },
        photoMd5s,
      })
    },
  })
}

function AddPhotosHandler({ albumId }: { albumId: string }) {
  const { ActionDispatcher } = usePhotoActionContext()
  const { mutate, isPending } = useAddPhotos()
  return (
    <ActionDispatcher
      action={ADD_PHOTOS_TO_ALBUM}
      loading={isPending}
      onClick={() => mutate(albumId)}
    />
  )
}

function AddPhotos() {
  const { ActionDispatcher, albumIdOrShareId, setActiveActionHandler } =
    useAlbumActionContext()
  const navigate = useNavigate()
  const authenticated = useAuthenticated() === 'TRUE'
  const [navigating, setNavigating] = useState(false)
  return (
    <ActionDispatcher
      action={ADD_PHOTOS}
      actionType={Album_AlbumAction.ADD_PHOTOS}
      loading={navigating}
      onClick={() => {
        if (authenticated) {
          navigate(`/photo?add-photos=${albumIdOrShareId}`)
          setNavigating(true)
        } else {
          setActiveActionHandler(() => (
            <LoginToContinue
              title={t`Log in to add your own photos`}
              url={`/share/${albumIdOrShareId}/addphotos`}
            />
          ))
        }
      }}
    />
  )
}

function FollowAlbum() {
  const { ActionDispatcher, albumIdOrShareId, setActiveActionHandler } =
    useAlbumActionContext()
  const authenticated = useAuthenticated() === 'TRUE'
  const navigate = useNavigate()
  const [isNavigating, setIsNavigating] = useState(false)
  const { mutate: followAlbum, isPending } = useConnectMutation(
    followAlbumDescriptor,
    {
      onSuccess: ({ albumId }) => {
        navigate(`/photo/album/${albumId}`)
        setIsNavigating(true)
      },
    },
  )
  return (
    <ActionDispatcher
      action={FOLLOW_ALBUM}
      actionType={Album_AlbumAction.FOLLOW_ALBUM}
      loading={isPending || isNavigating}
      onClick={() => {
        if (authenticated) {
          followAlbum({
            shareId: albumIdOrShareId,
          })
        } else {
          setActiveActionHandler(() => (
            <LoginToContinue
              title={t`Would you like to follow this album?`}
              url={`/share/${albumIdOrShareId}/follow`}
            />
          ))
        }
      }}
    />
  )
}

function LeaveAlbumHandler() {
  const { albumIdOrShareId, cancelActiveAction } = useAlbumActionContext()
  const navigate = useNavigate()
  const afterLeavePath = popPath(location.pathname)
  const { mutate: leaveAlbum, isPending } = useConnectMutation(
    unshareAlbumDescriptor,
    {
      onSuccess: () => {
        navigate(afterLeavePath, {
          replace: true,
        })
      },
    },
  )
  return (
    <LeaveAlbumDialog
      open
      isLoading={isPending}
      onLeaveAlbum={() => {
        leaveAlbum({
          albumId: albumIdOrShareId,
        })
      }}
      onClose={cancelActiveAction}
    />
  )
}

function ShareAlbum() {
  const { albumIdOrShareId } = useAlbumActionContext()
  const shareId = useAlbum(albumIdOrShareId).data?.shareId
  const actionFilter = shareId
    ? undefined // if already shared, we don't need to filter on the share action
    : Album_AlbumAction.SHARE_ALBUM
  return (
    <AlbumActionTrigger
      action={SHARE_ALBUM}
      actionFilter={actionFilter}
      handler={ShareAlbumHandler}
    />
  )
}

function ShareAlbumHandler() {
  const { albumIdOrShareId, cancelActiveAction } = useAlbumActionContext()
  return (
    <ShareAlbumDialog
      forceOpen
      albumId={albumIdOrShareId}
      onOpenChange={cancelActiveAction}
      triggerElement={undefined}
    />
  )
}

function DeleteAlbumHandler() {
  const { albumIdOrShareId, cancelActiveAction } = useAlbumActionContext()
  const navigate = useNavigate()
  const location = useLocation()
  const afterDeletePath = popPath(location.pathname)

  const { mutate: deleteAlbum, isPending } = useConnectMutation(
    deleteAlbumDescriptor,
    {
      onSuccess: () =>
        navigate(
          afterDeletePath === '/share' ? '/photo/all_shared' : afterDeletePath,
        ),
    },
  )
  return (
    <DeleteAlbumDialog
      open
      onClose={cancelActiveAction}
      isLoading={isPending}
      onDeleteAlbum={() =>
        deleteAlbum({
          albumId: albumIdOrShareId,
        })
      }
    />
  )
}

function AlbumActionTrigger({
  action,
  actionFilter,
  handler,
}: {
  action: Action
  actionFilter: Album_AlbumAction | undefined
  handler: FC
}) {
  const { ActionDispatcher, setActiveActionHandler } = useAlbumActionContext()
  return (
    <ActionDispatcher
      action={action}
      actionType={actionFilter}
      loading={false}
      onClick={() => setActiveActionHandler(handler)}
    />
  )
}

export const AlbumAction = {
  Provider: AlbumActionProvider,

  AddPhotos,
  DownloadAlbum,
  ShareAlbum,
  FollowAlbum,

  DeleteAlbum: () => (
    <AlbumActionTrigger
      action={DELETE_ALBUM}
      actionFilter={Album_AlbumAction.DELETE_ALBUM}
      handler={DeleteAlbumHandler}
    />
  ),
  LeaveAlbum: () => (
    <AlbumActionTrigger
      action={LEAVE_ALBUM}
      actionFilter={Album_AlbumAction.LEAVE_ALBUM}
      handler={LeaveAlbumHandler}
    />
  ),
}
